import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './App.css';
import logo from './assets/logo.png';
import { submitContactRequest } from './api/contact';
import SeoManager from './components/SeoManager';
import {
  ensureLocalizedPath,
  getCurrentPublicLocale,
  getPublicLocaleFromI18nLanguage,
  persistPublicLocale,
  switchToLocaleUrl,
} from './utils/locale';

const projectMedia = [
  {
    type: 'done',
    images: ['/images/arev1.jpg', '/images/arev2.jpg', '/images/arev3.jpg'],
  },
  {
    type: 'done',
    images: ['/images/nairi1.png', '/images/nairi2.jpg', '/images/nairi3.jpg'],
  },
  {
    type: 'done',
    images: ['/images/monte1.jpg', '/images/monte2.jpg', '/images/monte3.jpg'],
  },
  {
    type: 'done',
    images: ['/images/mantashyan1.jpg', '/images/mantashyan2.jpg', '/images/mantashyan3.jpg'],
  },
  {
    type: 'progress',
    images: [
      '/images/monument1.jpg',
      '/images/monument2.jpg',
      '/images/monument3.jpg',
      '/images/monument4.jpg',
      '/images/monument5.jpg',
    ],
  },
  {
    type: 'progress',
    images: ['/images/nor_gyux1.jpg', '/images/nor_gyux2.jpg', '/images/nor_gyux3.jpg'],
  },
  {
    type: 'progress',
    images: ['/images/capitalpark1.jpg', '/images/capitalpark2.jpg', '/images/capitalpark3.jpg'],
  },
];

const partners = [
  { name: 'PERI', logo: '/logos/peri.jpg' },
  { name: 'YM Materials', logo: '/logos/ym.jpg' },
  { name: 'Cascade Group', logo: '/logos/kaskad.png' },
  { name: 'ACBA Bank', logo: '/logos/acba.png' },
  { name: 'Made to Cake', logo: '/logos/m2m.png' },
  { name: 'Double V Construction', logo: '/logos/doublev.jpg' },
  { name: 'BETPRO', logo: null },
];

const clients = [
  { name: 'Kerama Marazzi', logo: '/logos/kerama.jpg' },
  { name: 'Harutyunyan Shin', logo: '/logos/harutyunyan.jpg' },
  { name: 'Bayazet Construction', logo: '/logos/bayazet.jpg' },
  { name: 'SOHO Construction', logo: '/logos/soho.png' },
  { name: 'Gazavik', logo: '/logos/gazavik.jfif' },
];

const initialContactForm = {
  name: '',
  phone: '',
  email: '',
  message: '',
  website: '',
};

function App() {
  const { t, i18n } = useTranslation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);
  const [activeSection, setActiveSection] = useState('home');
  const [langOpen, setLangOpen] = useState(false);
  const [contactForm, setContactForm] = useState(initialContactForm);
  const [contactFieldErrors, setContactFieldErrors] = useState({});
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [contactStatus, setContactStatus] = useState(null);

  const currentPublicLocale = getPublicLocaleFromI18nLanguage(i18n.language);

  const navItems = useMemo(
    () => [
      { id: 'home', label: t('nav.home') },
      { id: 'about', label: t('nav.about') },
      { id: 'services', label: t('nav.services') },
      { id: 'projects', label: t('nav.projects') },
      { id: 'advantages', label: t('nav.advantages') },
      { id: 'partners', label: t('nav.partners') },
      { id: 'contact', label: t('nav.contact') },
    ],
    [t]
  );

  const stats = t('stats', { returnObjects: true });
  const serviceGroups = t('services.groups', { returnObjects: true });
  const principles = t('about.principles', { returnObjects: true });
  const advantages = t('advantages.items', { returnObjects: true });

  const projects = useMemo(() => {
    const translatedProjects = t('projects.items', { returnObjects: true });

    return translatedProjects.map((project, index) => ({
      ...project,
      type: projectMedia[index]?.type || 'done',
      images: projectMedia[index]?.images || [],
    }));
  }, [t, i18n.language]);

  const completedProjects = useMemo(
    () => projects.filter((project) => project.type === 'done'),
    [projects]
  );

  const currentProjects = useMemo(
    () => projects.filter((project) => project.type === 'progress'),
    [projects]
  );

  useEffect(() => {
    const publicLocale = getCurrentPublicLocale(i18n.language);
    persistPublicLocale(publicLocale);
    ensureLocalizedPath(publicLocale);
  }, [i18n.language]);

  useEffect(() => {
    const sections = navItems
      .map(({ id }) => document.getElementById(id))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visible?.target?.id) {
          setActiveSection(visible.target.id);
        }
      },
      {
        threshold: [0.3, 0.55, 0.8],
        rootMargin: '-18% 0px -30% 0px',
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [navItems]);

  useEffect(() => {
    document.body.style.overflow = selectedProject ? 'hidden' : 'auto';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedProject]);

  const scrollToSection = (id) => {
    const section = document.getElementById(id);

    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMenuOpen(false);
    }
  };

  const changeLanguage = (language) => {
    i18n.changeLanguage(language);
    persistPublicLocale(getPublicLocaleFromI18nLanguage(language));
    switchToLocaleUrl(language);
    setLangOpen(false);
  };

  const openProject = (project) => {
    setSelectedProject(project);
    setCurrentImage(0);
  };

  const closeProject = () => {
    setSelectedProject(null);
    setCurrentImage(0);
  };

  const showPrevImage = () => {
    if (!selectedProject) return;

    setCurrentImage((prev) =>
      prev === 0 ? selectedProject.images.length - 1 : prev - 1
    );
  };

  const showNextImage = () => {
    if (!selectedProject) return;

    setCurrentImage((prev) =>
      prev === selectedProject.images.length - 1 ? 0 : prev + 1
    );
  };

  const handleContactChange = (event) => {
    const { name, value } = event.target;
    const nextValue = name === 'phone'
      ? value.replace(/[^0-9+\-()\s]/g, '')
      : value;

    setContactForm((prev) => ({
      ...prev,
      [name]: nextValue,
    }));

    if (contactFieldErrors[name]) {
      setContactFieldErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors[name];
        return nextErrors;
      });
    }
  };

  const validateContactForm = () => {
    const phonePattern = /^[0-9+\-()\s]+$/;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const errors = {};

    if (!contactForm.name.trim()) {
      errors.name = 'contact.validation.nameRequired';
    }

    if (!contactForm.phone.trim()) {
      errors.phone = 'contact.validation.phoneRequired';
    } else if (!phonePattern.test(contactForm.phone.trim())) {
      errors.phone = 'contact.validation.phoneInvalid';
    }

    if (contactForm.email.trim() && !emailPattern.test(contactForm.email.trim())) {
      errors.email = 'contact.validation.emailInvalid';
    }

    if (!contactForm.message.trim()) {
      errors.message = 'contact.validation.messageRequired';
    }

    return errors;
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateContactForm();

    if (Object.keys(validationErrors).length > 0) {
      setContactFieldErrors(validationErrors);
      setContactStatus(null);
      return;
    }

    setIsSubmittingContact(true);
    setContactFieldErrors({});
    setContactStatus(null);

    try {
      const response = await submitContactRequest(contactForm, currentPublicLocale);

      setContactForm(initialContactForm);
      setContactFieldErrors({});
      setContactStatus({
        type: 'success',
        titleKey: 'contact.successTitle',
        messageKey: response?.message_key || 'contact.successMessage',
      });
    } catch (error) {
      if (error?.status === 422 && error?.data?.errors) {
        const serverFieldErrorKeys = {
          name: 'contact.validation.nameRequired',
          phone: 'contact.validation.phoneInvalid',
          email: 'contact.validation.emailInvalid',
          message: 'contact.validation.messageRequired',
        };
        const serverErrors = Object.entries(error.data.errors).reduce(
          (errors, [field, messages]) => ({
            ...errors,
            [field]: serverFieldErrorKeys[field] || (
              Array.isArray(messages) && messages.length
                ? messages[0]
                : 'contact.validation.invalidField'
            ),
          }),
          {}
        );

        setContactFieldErrors(serverErrors);
        setContactStatus(null);
        return;
      }

      setContactStatus({
        type: 'error',
        titleKey: 'contact.errorTitle',
        messageKey: error?.messageKey || 'contact.sendError',
      });
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const renderProjectCards = (list) => (
    <div className="cardsGrid projectsGrid">
      {list.map((project) => (
        <article key={project.name} className="projectCard">
          <button
            type="button"
            className="projectOpenButton"
            onClick={() => openProject(project)}
          >
            <div className="projectImageWrap">
              <img
                src={project.images[0]}
                alt={`${project.name} - ${t('projects.imageAlt')}`}
                className="projectImage"
              />

              <span
                className={
                  project.type === 'progress'
                    ? 'projectStatus progress'
                    : 'projectStatus'
                }
              >
                {project.status}
              </span>
            </div>

            <div className="projectBody">
              <h3>{project.name}</h3>
              <span>{t('projects.view')}</span>
            </div>
          </button>
        </article>
      ))}
    </div>
  );

  return (
    <div className="siteShell">
      <SeoManager />

      <header className="topbar">
        <button
          className="brandButton"
          type="button"
          onClick={() => scrollToSection('home')}
          aria-label={t('nav.home')}
        >
          <span className="brandLogoWrap">
            <img
              src={logo}
              alt={t('seo.logoAlt')}
              className="brandLogo"
            />
          </span>

          <div className="brandText">
            <strong>{t('brand.short')}</strong>
            <span>{t('brand.construction')}</span>
          </div>
        </button>

        <nav className="desktopNav">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className={activeSection === item.id ? 'navLink active' : 'navLink'}
              onClick={() => scrollToSection(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="headerLangBox">
          <button
            type="button"
            className="headerLangMain"
            onClick={() => setLangOpen((prev) => !prev)}
          >
            {currentPublicLocale.toUpperCase()}
          </button>

          {langOpen && (
            <div className="headerLangList">
              <button type="button" onClick={() => changeLanguage('am')}>HY</button>
              <button type="button" onClick={() => changeLanguage('ru')}>RU</button>
              <button type="button" onClick={() => changeLanguage('en')}>EN</button>
            </div>
          )}
        </div>

        <button
          type="button"
          className={menuOpen ? 'menuToggle open' : 'menuToggle'}
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="Menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </header>

      <div className={menuOpen ? 'mobilePanel open' : 'mobilePanel'}>
        {navItems.map((item) => (
          <button
            key={item.id}
            type="button"
            className={activeSection === item.id ? 'mobileLink active' : 'mobileLink'}
            onClick={() => scrollToSection(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <main>
        <section id="home" className="heroSection">
          <div
            className="heroBackdrop"
            style={{ backgroundImage: 'url(/hero.jpg)' }}
          ></div>

          <div className="heroOverlay"></div>

          <div className="sectionContainer heroGrid">
            <div className="heroCopy">
              <div className="heroLogoBadge">
                <img
                  src={logo}
                  alt={t('seo.logoAlt')}
                  className="heroLogo"
                />
              </div>

              <h1 className="heroTitleAm">
                {t('hero.title')}
              </h1>

              <p className="heroTagline">
                {t('hero.tagline')}
              </p>

              <p className="heroDescription">
                {t('hero.description')}
              </p>

              <div className="heroActions">
                <button
                  type="button"
                  className="primaryBtn"
                  onClick={() => scrollToSection('projects')}
                >
                  {t('hero.projectsButton')}
                </button>

                <button
                  type="button"
                  className="secondaryBtn"
                  onClick={() => scrollToSection('contact')}
                >
                  {t('hero.contactButton')}
                </button>
              </div>
            </div>

            <div className="heroCard heroStatsCard">
              <div className="statsGrid">
                {stats.map((item) => (
                  <div key={item.label} className="statCard">
                    <strong>{item.value}</strong>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="about" className="contentSection lightSection">
          <div className="sectionContainer twoColumnLayout">
            <div>
              <div className="sectionHeader">
                <h2 className="sectionTitle">
                  {t('about.title')}
                </h2>
              </div>

              <div className="aboutText">
                {t('about.paragraphs', { returnObjects: true }).map((paragraph) => (
                  <p key={paragraph}>
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            <div className="infoCard principlesCard">
              <h3>{t('about.principlesTitle')}</h3>

              <div className="principlesGrid">
                {principles.map((item) => (
                  <div key={item.title} className="principleItem">
                    <h4>{item.title}</h4>
                    <p>{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="services" className="contentSection darkSection">
          <div className="sectionContainer">
            <div className="sectionHeader centered">
              <h2 className="sectionTitle light">{t('services.title')}</h2>

              <p className="sectionLead centeredLead lightLead">
                {t('services.lead')}
              </p>
            </div>

            <div className="serviceGroupsGrid">
              {serviceGroups.map((group) => (
                <article
                  key={group.category}
                  className="serviceGroupCard"
                >
                  <div className="serviceGroupHeader">
                    <span className="serviceGroupLine"></span>

                    <h3>{group.category}</h3>
                  </div>

                  <div className="serviceGroupItems">
                    {group.items.map((service) => (
                      <div
                        key={service.title}
                        className="serviceMiniCard"
                      >
                        <h4>{service.title}</h4>
                        <p>{service.text}</p>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="projects" className="contentSection lightSection">
          <div className="sectionContainer">
            <div className="sectionHeader centered">
              <h2 className="sectionTitle">{t('projects.title')}</h2>
            </div>

            <div className="projectGroup">
              <div className="projectGroupHeader">
                <span>{t('projects.completed')}</span>
              </div>

              {renderProjectCards(completedProjects)}
            </div>

            <div className="projectGroup">
              <div className="projectGroupHeader">
                <span>{t('projects.current')}</span>
              </div>

              {renderProjectCards(currentProjects)}
            </div>
          </div>
        </section>

        <section id="advantages" className="contentSection accentSection">
          <div className="sectionContainer">
            <div className="sectionHeader centered">
              <h2 className="sectionTitle">{t('advantages.title')}</h2>
            </div>

            <div className="advantagesGrid">
              {advantages.map((item) => (
                <div key={item} className="advantageCard">
                  <span className="advantageMark"></span>
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="partners" className="contentSection lightSection logosSection">
          <div className="sectionContainer">
            <div className="logoBlock">
              <h3>{t('partners.title')}</h3>

              <div className="logoGrid">
                {partners.map((partner) => (
                  <div
                    key={partner.name}
                    className={
                      partner.logo
                        ? 'logoCard'
                        : 'logoCard logoCardTextOnly'
                    }
                  >
                    {partner.logo ? (
                      <img src={partner.logo} alt={`${partner.name} - ${t('partners.logoAlt')}`} />
                    ) : (
                      <div className="logoTextOnly">
                        {partner.name}
                      </div>
                    )}

                    <span>{partner.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="logoBlock spacedTop">
              <h3>{t('partners.clientsTitle')}</h3>

              <div className="logoGrid">
                {clients.map((client) => (
                  <div key={client.name} className="logoCard">
                    <img src={client.logo} alt={`${client.name} - ${t('partners.clientLogoAlt')}`} />
                    <span>{client.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section
          id="contact"
          className="contentSection contactLuxurySection"
        >
          <div className="sectionContainer">
            <div className="contactLuxuryHeader">
              <span>{t('contact.badge')}</span>

              <h2>{t('contact.title')}</h2>

              <p>{t('contact.lead')}</p>
            </div>

            <div className="contactLuxuryGrid">
              <div className="contactPanel">
                <div className="contactPanelTop">
                  <h3>{t('contact.detailsTitle')}</h3>

                  <p>{t('contact.hours')}</p>
                </div>

                <div className="contactRows">
                  <a
                    href="tel:+37443537070"
                    className="contactRow"
                  >
                    <span>{t('contact.phone')}</span>
                    <strong>+374 43537070</strong>
                  </a>

                  <a
                    href="mailto:info@rmgconstruction.am"
                    className="contactRow"
                  >
                    <span>{t('contact.email')}</span>
                    <strong>info@rmgconstruction.am</strong>
                  </a>

                  <div className="contactRow">
                    <span>{t('contact.address')}</span>
                    <strong>{t('contact.addressValue')}</strong>
                  </div>
                </div>

                <form className="contactForm" onSubmit={handleContactSubmit} noValidate>
                  <div className="contactFormHeader">
                    <h3>{t('contact.formTitle')}</h3>
                    <p>{t('contact.formLead')}</p>
                  </div>

                  <input
                    className="formHidden"
                    type="text"
                    name="website"
                    value={contactForm.website}
                    onChange={handleContactChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />

                  <div className="formGrid">
                    <div className="formField">
                      <input
                        type="text"
                        name="name"
                        value={contactForm.name}
                        onChange={handleContactChange}
                        placeholder={t('contact.namePlaceholder')}
                        className={contactFieldErrors.name ? 'fieldInvalid' : ''}
                        aria-invalid={Boolean(contactFieldErrors.name)}
                        required
                      />
                      {contactFieldErrors.name && (
                        <span className="fieldError">
                          {t(contactFieldErrors.name, { defaultValue: contactFieldErrors.name })}
                        </span>
                      )}
                    </div>

                    <div className="formField">
                      <input
                        type="tel"
                        name="phone"
                        value={contactForm.phone}
                        onChange={handleContactChange}
                        placeholder={t('contact.phonePlaceholder')}
                        inputMode="tel"
                        maxLength="25"
                        className={contactFieldErrors.phone ? 'fieldInvalid' : ''}
                        aria-invalid={Boolean(contactFieldErrors.phone)}
                        required
                      />
                      {contactFieldErrors.phone && (
                        <span className="fieldError">
                          {t(contactFieldErrors.phone, { defaultValue: contactFieldErrors.phone })}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="formField">
                    <input
                      type="email"
                      name="email"
                      value={contactForm.email}
                      onChange={handleContactChange}
                      placeholder={t('contact.emailPlaceholder')}
                      className={contactFieldErrors.email ? 'fieldInvalid' : ''}
                      aria-invalid={Boolean(contactFieldErrors.email)}
                    />
                    {contactFieldErrors.email && (
                      <span className="fieldError">
                        {t(contactFieldErrors.email, { defaultValue: contactFieldErrors.email })}
                      </span>
                    )}
                  </div>

                  <div className="formField">
                    <textarea
                      name="message"
                      value={contactForm.message}
                      onChange={handleContactChange}
                      rows="5"
                      placeholder={t('contact.messagePlaceholder')}
                      className={contactFieldErrors.message ? 'fieldInvalid' : ''}
                      aria-invalid={Boolean(contactFieldErrors.message)}
                      required
                    ></textarea>
                    {contactFieldErrors.message && (
                      <span className="fieldError">
                        {t(contactFieldErrors.message, { defaultValue: contactFieldErrors.message })}
                      </span>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="contactSubmitBtn"
                    disabled={isSubmittingContact}
                  >
                    {isSubmittingContact ? t('contact.loading') : t('contact.submit')}
                  </button>
                </form>

                <div className="followSocialBlock">
                  <span className="followUsTitle">
                    {t('contact.follow')}
                  </span>

                  <div className="socialCleanGrid">
                    <a
                      href="https://www.facebook.com/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Facebook
                    </a>

                    <a
                      href="https://www.instagram.com/rmgconstruction.am/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Instagram
                    </a>

                    <a
                      href="https://wa.me/37443537070"
                      target="_blank"
                      rel="noreferrer"
                    >
                      WhatsApp
                    </a>

                    <a
                      href="viber://chat?number=%2B37443537070"
                    >
                      Viber
                    </a>
                  </div>
                </div>
              </div>

              <div className="mapPanel">
                <iframe
                  title="RMG Construction Map"
                  src="https://www.google.com/maps?q=40.214503,44.481525&z=17&output=embed"
                  loading="lazy"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </section>
      </main>

      {contactStatus && (
        <div
          className="successModalOverlay"
          onClick={() => setContactStatus(null)}
        >
          <div
            className="successModal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="successModalClose"
              onClick={() => setContactStatus(null)}
            >
              ×
            </button>

            <div className="successModalIcon">
              {contactStatus.type === 'success' ? '✓' : '!'}
            </div>

            <h3>{t(contactStatus.titleKey)}</h3>

            <p>{t(contactStatus.messageKey)}</p>
          </div>
        </div>
      )}

      <footer className="footer">
        <div className="sectionContainer footerInner">
          <div className="footerBrand">
            <div className="footerLogoWrap">
              <img
                src={logo}
                alt={t('seo.logoAlt')}
                className="footerLogo"
              />
            </div>

            <div>
              <strong>{t('brand.name')}</strong>
              <p>{t('brand.tagline')}</p>
            </div>
          </div>

          <div className="footerCopy">
            {t('brand.rights')}
          </div>
        </div>
      </footer>

      {selectedProject && (
        <div className="modalOverlay">
          <div className="modalCard">
            <button
              type="button"
              className="modalClose"
              onClick={closeProject}
            >
              ×
            </button>

            <div className="modalMedia">
              <button
                type="button"
                className="sliderBtn"
                onClick={showPrevImage}
              >
                ‹
              </button>

              <img
                src={selectedProject.images[currentImage]}
                alt={`${selectedProject.name} ${currentImage + 1} - ${t('projects.imageAlt')}`}
                className="modalImage"
              />

              <button
                type="button"
                className="sliderBtn"
                onClick={showNextImage}
              >
                ›
              </button>
            </div>

            <div className="modalContent">
              <span className="projectStatus static">
                {selectedProject.status}
              </span>

              <h3>{selectedProject.name}</h3>

              <p>{selectedProject.description}</p>

              <div className="modalMeta">
                <div>
                  <span>{t('projects.metaLocation')}</span>
                  <strong>{selectedProject.location}</strong>
                </div>

                <div>
                  <span>{t('projects.metaDeveloper')}</span>
                  <strong>{selectedProject.developer}</strong>
                </div>

                <div>
                  <span>{t('projects.metaWork')}</span>
                  <strong>{selectedProject.work}</strong>
                </div>
              </div>

              <div className="thumbRow">
                {selectedProject.images.map((image, index) => (
                  <button
                    key={image}
                    type="button"
                    className={currentImage === index ? 'thumb active' : 'thumb'}
                    onClick={() => setCurrentImage(index)}
                  >
                    <img
                      src={image}
                      alt={`${selectedProject.name} ${index + 1} - ${t('projects.imageAlt')}`}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
